/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback } from 'react';

import { useToast } from '@dashboard/hooks';
import { ClientException } from '@dashboard/library';
import { UpdateAgentMutation, UpdateAgentMutationVariables } from '@generated/UpdateAgentMutation';
import { UserFullFragment } from '@generated/UserFullFragment';
import { UPDATE_AGENT_MUTATION } from '@graphql/mutations/UpdateAgentMutation';
import { getExternalErrorTranslationStrings } from '@helpers/errors.helpers';
import { getTrackSettingsProperties } from '@helpers/tracker.helpers';
import { useGraphMutation } from '@hooks/useMutation';
import { useTracker } from '@hooks/useTracker/useTracker';
import { useTranslation } from 'react-i18next';

export function useUpdateDefaultNumber(user: UserFullFragment, isOwnUser: boolean) {
  const { t } = useTranslation();
  const [mutate] = useGraphMutation<UpdateAgentMutation, UpdateAgentMutationVariables>(
    UPDATE_AGENT_MUTATION
  );
  const { track } = useTracker();
  const toast = useToast();

  const onUpdateDefaultNumber = useCallback(
    async (defaultOutPhoneNumberID: UserFullFragment['defaultOutPhoneNumberID']) => {
      try {
        await mutate({
          variables: {
            input: {
              ID: user.ID,
              defaultOutPhoneNumberID,
            },
          },
          optimisticResponse: {
            updateAgent: {
              ...user,
              defaultOutPhoneNumberID,
            },
          },
        });
        toast.show({
          variant: 'success',
          message: t('success_feedback_messages.update_success', {
            name: t('user_profile.numbers_and_teams.associated_numbers.tags.default_number'),
          }),
        });
        track(
          getTrackSettingsProperties({
            event: 'user_settings_edited',
            settingsSection: 'Numbers & Teams',
            setting: 'Associated numbers',
            field: 'number',
            action: 'set_as_default',
            isOwnUser,
          })
        );
      } catch (e) {
        const { error } = e as ClientException;

        toast.show({
          variant: 'error',
          message: t(getExternalErrorTranslationStrings(error)),
        });
      }
    },
    [isOwnUser, mutate, t, toast, track, user]
  );

  return { onUpdateDefaultNumber };
}
